import { Button } from '#atoms';
import usePatchCurrentStepMutation from '#hooks/queries/onboard/usePatchCurrentStepMutation';
import { FloatButtonContainer } from '#molecules';
import { onBoardStateAtom } from '#pages/OnBoardStep/OnBoardStep.atoms';
import { useOnBoardStep } from '#pages/OnBoardStep/OnBoardStep.hooks';
import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';

interface Step3Props {}

const Step3 = ({}: Step3Props) => {
  const [selected, setSelected] = useState<number>();

  const { mutateAsync: patchCurrentStep } = usePatchCurrentStepMutation();
  const { setStep } = useOnBoardStep();

  const setOnboardState = useSetRecoilState(onBoardStateAtom);

  const itemList = [
    {
      icon: (isSelected: boolean) => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
        >
          <path
            d="M12.5 15.6667C11.58 15.6667 10.8333 16.4133 10.8333 17.3333C10.8333 18.2533 11.58 19 12.5 19C13.42 19 14.1666 18.2533 14.1666 17.3333C14.1666 16.4133 13.42 15.6667 12.5 15.6667ZM20.5 15.6667C19.58 15.6667 18.8333 16.4133 18.8333 17.3333C18.8333 18.2533 19.58 19 20.5 19C21.42 19 22.1666 18.2533 22.1666 17.3333C22.1666 16.4133 21.42 15.6667 20.5 15.6667ZM16.5 2.66666C9.13996 2.66666 3.16663 8.64 3.16663 16C3.16663 23.36 9.13996 29.3333 16.5 29.3333C23.86 29.3333 29.8333 23.36 29.8333 16C29.8333 8.64 23.86 2.66666 16.5 2.66666ZM16.5 26.6667C10.62 26.6667 5.83329 21.88 5.83329 16C5.83329 15.6133 5.85996 15.2267 5.89996 14.8533C9.04663 13.4533 11.54 10.88 12.8466 7.69333C15.26 11.1067 19.2333 13.3333 23.7266 13.3333C24.7666 13.3333 25.7666 13.2133 26.7266 12.9867C27.0066 13.9333 27.1666 14.9467 27.1666 16C27.1666 21.88 22.38 26.6667 16.5 26.6667Z"
            fill={isSelected ? '#EAE8ED' : '#746D87'}
          />
        </svg>
      ),
      text1: '회원 관리 기능만 필요해요',
      text2: '회원 관리에 필요한 필수 정보만 입력합니다.'
    },
    {
      icon: (isSelected: boolean) => (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="33"
          height="32"
          viewBox="0 0 33 32"
          fill="none"
        >
          <path
            d="M25.8334 12L27.5 8.33334L31.1667 6.66668L27.5 5.00001L25.8334 1.33334L24.1667 5.00001L20.5 6.66668L24.1667 8.33334L25.8334 12ZM15.8334 12.6667L12.5 5.33334L9.16671 12.6667L1.83337 16L9.16671 19.3333L12.5 26.6667L15.8334 19.3333L23.1667 16L15.8334 12.6667ZM25.8334 20L24.1667 23.6667L20.5 25.3333L24.1667 27L25.8334 30.6667L27.5 27L31.1667 25.3333L27.5 23.6667L25.8334 20Z"
            fill={isSelected ? '#EAE8ED' : '#746D87'}
          />
        </svg>
      ),
      text1: '신규 회원 창출 기능도 필요해요',
      text2:
        '나만의 프로필을 생성하여 구글, 네이버에 노출하고 \n 인스타그램에 공유할 수 있습니다.'
    }
  ];

  const buttonText = selected === 0 ? '기본정보' : '프로필';

  const disabled = selected === undefined;

  const onNext = () => {
    if (selected === 0) {
      patchCurrentStep(8);
      setStep(8);
      setOnboardState(prev => ({
        ...prev,
        feature: '기본기능'
      }));
    } else {
      patchCurrentStep(4);
      setStep(4);
      setOnboardState(prev => ({
        ...prev,
        feature: '신규회원창출기능'
      }));
    }
  };

  return (
    <div className="relative h-[calc(100dvh-139px)] flex flex-col justify-between">
      <div>
        <Title>사용하실 기능을{'\n'}선택해 주세요</Title>
        <div className="flex flex-col gap-8">
          {itemList.map((item, index) => (
            <Item
              onClick={() => setSelected(index)}
              selected={selected === index}
              key={item.text1}
            >
              <div className="flex flex-col gap-16">
                <div className="flex justify-center">
                  {item.icon(selected === index)}
                </div>
                <div className="flex flex-col gap-6">
                  <Text1 selected={selected === index}>{item.text1}</Text1>
                  <Text2>{item.text2}</Text2>
                </div>
              </div>
            </Item>
          ))}
        </div>
      </div>

      <FloatButtonContainer>
        <Button onClick={onNext} disabled={disabled}>
          {buttonText} 입력하기
        </Button>
      </FloatButtonContainer>
    </div>
  );
};

export const Title = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.gray15};
  white-space: pre-wrap;
  margin-bottom: 40px;
`;

const Item = styled.div<{ selected: boolean }>`
  width: 100%;
  padding: 32px 20px 32px 20px;
  gap: 16px;
  border-radius: 8px;
  opacity: 0px;
  border: 2px solid ${({ selected }) => (selected ? '#7750E9' : '#2e2b36')};
`;

const Text1 = styled.div<{ selected: boolean }>`
  color: ${({ selected }) => (selected ? '#EAE8ED' : '#8E899F')};
  //styleName: Body 2/SB;
  font-family: Pretendard Variable;
  font-size: 16px;
  font-weight: 600;
  line-height: 21px;
  text-align: center;
`;

const Text2 = styled.div`
  //styleName: Caption 1/R;
  font-family: Pretendard Variable;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
  color: #8e899f;
  white-space: pre-wrap;
`;

export default Step3;
